import moment from 'moment';
import { generateInvoicePDFDirect } from './pdfGenerator';
import { getAllSellersList, getSellerAndAddressBySellerId } from '../store/actions/company';
import { parseCompanyAndSeller } from '../components/Accounts/Invoices/utils/InvoiceDetailsPopup.utils';

export const NO_CHARGES_POSTED = 'No charges posted';
const WALLET_RECHARGE_TASK_TYPE = 'wallet recharge';

export const formatInvoiceDate = (date) => (date ? moment(date).format('MM-DD-YYYY') : '-');

export const formatInvoiceAmount = (amount) => {
    if (amount === null || amount === undefined || amount === '' || Number.isNaN(Number(amount))) {
        return '-';
    }
    return `$${Number(amount).toFixed(2)}`;
};

// Post charge amounts come back formatted, e.g. "$12.50".
export const parseChargeAmount = (value) => {
    const amount = Number(String(value ?? '').replaceAll(/[^\d.-]/g, ''));
    return Number.isFinite(amount) ? amount : 0;
};

// Wallet Recharge (credit / top-up) tasks are never invoiced.
export const isInvoiceableTask = (task) =>
    String(task?.taskTypeName || '').trim().toLowerCase() !== WALLET_RECHARGE_TASK_TYPE;

// GetSellersByCompanyIds returns "Seller (Company)" in sellerAndCompany.
const getCompanyFromSellerAndCompany = (sellerAndCompany) => {
    const match = /\(([^()]+)\)\s*$/.exec(String(sellerAndCompany ?? ''));
    return match ? match[1].trim() : '';
};

// GetSellerAndAddressBySellerId lists the seller's companies as { CompanyIds, CompanyName }.
const getCompanyFromSellerDetail = (sellerDetail, companyId) => {
    const company = (sellerDetail?.selectedCompanyIds || []).find((item) => Number(item?.CompanyIds) === Number(companyId));
    return String(company?.CompanyName ?? '').trim();
};

/**
 * Bill To address lines from the seller's default address (else the first one):
 * street lines, "City, State Zip", country.
 */
export const getSellerAddressLines = (addresses) => {
    const list = Array.isArray(addresses) ? addresses : [];
    const address = list.find((item) => Number(item?.isDefault) === 1) || list[0];
    if (!address) return [];

    const clean = (value) => String(value ?? '').trim();
    const cityState = [address.city, address.state].map(clean).filter(Boolean).join(', ');
    const cityStateZip = [cityState, clean(address.zipCode)].filter(Boolean).join(' ');
    return [address.addressFirst, address.addressSecond, address.addressThird, cityStateZip, address.country]
        .map(clean)
        .filter(Boolean);
};

// Runs a lookup and never rejects, so one failed source does not drop the other.
const settle = (lookup) =>
    Promise.resolve()
        .then(lookup)
        .catch((error) => {
            console.error('Failed to load seller details for invoice', error);
            return null;
        });

/**
 * Bill To (company, seller, seller code, address) for a task, looked up when the invoice is generated.
 * @param {Object} task - Task from GetTaskById (companyId, sellerId)
 * @param {string} companySellerName - Fallback "Company/Seller" label (task listing row)
 */
export const resolveTaskBillTo = async (task, companySellerName = '') => {
    const fallback = parseCompanyAndSeller(companySellerName);
    const billTo = { companyName: fallback.company, sellerName: fallback.seller, sellerCode: '', addressLines: [] };
    if (!task?.companyId) return billTo;

    const [sellersResponse, sellerDetailResponse] = await Promise.all([
        settle(() => getAllSellersList({ companyIds: [task.companyId] })),
        // Id 0 makes the API fall back to the logged-in user's seller, so only ask for a real seller.
        Number(task.sellerId) > 0 ? settle(() => getSellerAndAddressBySellerId(task.sellerId)) : null,
    ]);

    const sellers = sellersResponse?.data?.Entity?.listSellerMasterOrdersEntity || [];
    const seller = sellers.find((item) => Number(item.id) === Number(task.sellerId));
    const sellerDetail = sellerDetailResponse?.data?.Status === 200 ? sellerDetailResponse.data.Entity : null;

    return {
        companyName:
            getCompanyFromSellerAndCompany(seller?.sellerAndCompany)
            || getCompanyFromSellerDetail(sellerDetail, task.companyId)
            || billTo.companyName,
        sellerName: seller?.newSellerName || sellerDetail?.newSellerName || billTo.sellerName,
        sellerCode: seller?.sellerCode || sellerDetail?.sellerCode || '',
        addressLines: getSellerAddressLines(sellerDetail?.AddressModels),
    };
};

/**
 * One invoice per posted charge, built from the task and its Post Charges history.
 */
export const buildTaskChargeInvoices = (task, billTo = {}) => {
    if (!task || !isInvoiceableTask(task)) return [];
    const taskId = task.id ?? task.taskId;

    return (task.postCharges || []).map((charge) => ({
        chargeId: charge.id,
        invoiceNumber: `INV-${taskId}-${charge.id}`,
        invoiceDate: charge.postChargeDate,
        description: charge.postChargeComment || `${task.taskTypeName} - ${task.taskTitle}`,
        amount: parseChargeAmount(charge.postChargeAmount),
        taskId,
        taskTitle: task.taskTitle,
        taskTypeName: task.taskTypeName,
        sku: task.sku || task.taskReferenceDetails?.returnProductSku,
        reference: task.taskReferenceDetails?.trackingNumber,
        companyName: billTo.companyName || '',
        sellerName: billTo.sellerName || '',
        sellerCode: billTo.sellerCode || '',
        addressLines: billTo.addressLines || [],
    }));
};

/**
 * Generates the task charge invoice with the existing invoice PDF template
 * (Task Details in place of Period / Cycle).
 */
export const generateTaskChargeInvoicePDF = (invoice, { print = false } = {}) =>
    generateInvoicePDFDirect(
        {
            invoiceNumber: invoice.invoiceNumber,
            invoiceDate: formatInvoiceDate(invoice.invoiceDate),
            companyName: invoice.companyName,
            sellerName: invoice.sellerName,
            sellerCode: invoice.sellerCode || 'N/A',
            billToAddressLines: invoice.addressLines || [],
        },
        [
            {
                id: invoice.chargeId,
                service: invoice.taskTypeName || 'Task Charge',
                sku: invoice.sku,
                ref: invoice.reference,
                qty: 1,
                rate: invoice.amount,
                amount: invoice.amount,
            },
        ],
        {
            subtotal: invoice.amount,
            totalAdditionalCharges: 0,
            tax: 0,
            totalDue: invoice.amount,
            paymentMethod: 'Debited from Wallet',
        },
        {
            displayCompany: invoice.companyName || 'N/A',
            displaySeller: invoice.sellerName || '',
            taskDetails: [
                `Task ID: ${invoice.taskId}`,
                `Task Type: ${invoice.taskTypeName || 'N/A'}`,
                `Task Title: ${invoice.taskTitle || 'N/A'}`,
                `Description: ${invoice.description || 'N/A'}`,
            ],
            hideEmptyColumns: true,
            print,
        }
    );

// Invoice for a Post Charges row, if the charge is invoiceable.
export const findInvoiceForCharge = (invoices, chargeId) =>
    (invoices || []).find((invoice) => Number(invoice.chargeId) === Number(chargeId));
